import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import comprasAnalisisService, {
  ComprasAnalisisItem,
  ComprasAnalisisStockNetoComposicion,
  ComprasAnalisisStockNetoDeposito,
} from '../../services/comprasAnalisisService';
import { getApiErrorMessage } from '../../utils/apiError';
import StockNetoDocumentosDialog, { StockNetoDocumentosTipo } from './StockNetoDocumentosDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  item: ComprasAnalisisItem | null;
}

const formatCantidad = (value: number, maxDecimals = 2): string =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(value);

const formatOptional = (value: number) => (value > 0 ? formatCantidad(value) : '—');

const cellSx = { whiteSpace: 'nowrap' as const, fontSize: '0.8125rem' };

const StockNetoComposicionDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ComprasAnalisisStockNetoComposicion | null>(null);
  const [documentosTipo, setDocumentosTipo] = useState<StockNetoDocumentosTipo | null>(null);

  useEffect(() => {
    if (!open || !item?.codigoArticulo) {
      setData(null);
      setError(null);
      setDocumentosTipo(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void comprasAnalisisService
      .getStockNetoComposicion(item.codigoArticulo)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, 'No se pudo cargar la composición del stock.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, item?.codigoArticulo]);

  const pendienteOcTotal = data?.stockPendienteOc ?? data?.totales.stockPendienteOcRecibir ?? 0;

  const renderDepositoRow = (row: ComprasAnalisisStockNetoDeposito) => (
    <TableRow key={`${row.nroUbicacion}-${row.nombreUbicacion}`}>
      <TableCell sx={cellSx}>
        {row.nombreUbicacion}
        {row.nroUbicacion > 0 ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            #{row.nroUbicacion}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell align="right" sx={cellSx}>{formatCantidad(row.cantidadActual)}</TableCell>
      <TableCell align="right" sx={cellSx}>{formatCantidad(row.stockReservado)}</TableCell>
      <TableCell align="right" sx={cellSx}>—</TableCell>
      <TableCell align="right" sx={cellSx}>—</TableCell>
      <TableCell align="right" sx={cellSx}>{formatCantidad(row.atp)}</TableCell>
      <TableCell align="right" sx={cellSx}>—</TableCell>
    </TableRow>
  );

  const renderTotalClickeable = (
    value: number,
    tipo: StockNetoDocumentosTipo,
    label: string,
  ) => {
    if (value <= 0) return formatCantidad(value);

    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        sx={{ fontWeight: 700, fontSize: 'inherit', cursor: 'pointer' }}
        onClick={() => setDocumentosTipo(tipo)}
        aria-label={`Ver detalle de ${label}`}
      >
        {formatCantidad(value)}
      </Link>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              Composición del stock neto
            </Typography>
            {item ? (
              <Typography variant="body2" color="text.secondary">
                {item.codigoArticulo} · {item.descripcion}
              </Typography>
            ) : null}
          </Stack>
          <IconButton aria-label="Cerrar" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : data ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Stock neto (sugerencia)
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="primary.main">
                    {formatCantidad(data.stockNeto, 3)} {data.umStock}
                  </Typography>
                </Box>
              </Paper>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Depósito</TableCell>
                      <TableCell align="right" sx={cellSx}>Cant. actual</TableCell>
                      <TableCell align="right" sx={cellSx}>Reservado</TableCell>
                      <TableCell align="right" sx={cellSx}>Producción</TableCell>
                      <TableCell align="right" sx={cellSx}>Pend. OC recibir</TableCell>
                      <TableCell align="right" sx={cellSx}>ATP</TableCell>
                      <TableCell align="right" sx={cellSx}>Stock neto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.depositos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Sin registros por depósito para este artículo.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.depositos.map((dep) => renderDepositoRow(dep))
                    )}
                    <TableRow selected sx={{ '& td': { fontWeight: 700 } }}>
                      <TableCell sx={cellSx}>{data.totales.nombreUbicacion}</TableCell>
                      <TableCell align="right" sx={cellSx}>{formatCantidad(data.totales.cantidadActual)}</TableCell>
                      <TableCell align="right" sx={cellSx}>
                        {renderTotalClickeable(data.totales.stockReservado, 'reservas', 'reservado')}
                      </TableCell>
                      <TableCell align="right" sx={cellSx}>{formatOptional(data.totales.stockProduccion)}</TableCell>
                      <TableCell align="right" sx={cellSx}>
                        {renderTotalClickeable(pendienteOcTotal, 'oc-pendientes', 'pendiente OC')}
                      </TableCell>
                      <TableCell align="right" sx={cellSx}>{formatCantidad(data.totales.atp)}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, color: 'primary.main' }}>
                        {formatCantidad(data.stockNeto, 3)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <StockNetoDocumentosDialog
        open={documentosTipo != null}
        onClose={() => setDocumentosTipo(null)}
        codigoArticulo={item?.codigoArticulo ?? ''}
        tipo={documentosTipo}
      />
    </>
  );
};

export default StockNetoComposicionDialog;
